import { 
  FirebaseApp, 
  initializeApp, 
  getApp,
  getApps,
  deleteApp
} from 'firebase/app';
import { 
  Auth, 
  getAuth, 
  signInWithCustomToken,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { 
  Firestore, 
  getFirestore,
  collection,
  doc
} from 'firebase/firestore';
import { 
  getStorage,
  ref,
  FirebaseStorage
} from 'firebase/storage';
import { FirebaseExternalConfig } from '../../types/whiteboardProps';

// Default Firebase configuration
// const defaultConfig = {
//   apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
//   authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
//   projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'edesis-whiteboard-self',
//   storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
//   messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
//   appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
// };

export class DynamicFirebaseService {
  private app: FirebaseApp;
  private auth: Auth;
  private firestore: Firestore;
  private storage: FirebaseStorage;
  private projectPrefix: string;
  private tokenApiRoot?: string;
  private getFirebaseToken?: () => Promise<string>;
  private tokenRefreshInterval: number;
  private tokenRefreshTimer?: NodeJS.Timeout;
  private appName: string;
  private isFetchingToken: boolean = false;
  
  constructor(config?: FirebaseExternalConfig) {
    // Generate unique app name for this instance
    this.appName = config?.firebaseConfig ? `external-app-${Date.now()}` : 'default';
    
    // Initialize Firebase app
    if (config?.firebaseConfig) {
      // Check if app already exists
      const existingApp = getApps().find(app => app.name === this.appName);
      if (existingApp) {
        this.app = existingApp;
      } else {
        this.app = initializeApp(config.firebaseConfig, this.appName);
      }
    } else {
      // Use default app
      this.app = getApp();
    }
    
    // Initialize services
    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
    this.storage = getStorage(this.app);
    
    // Set configuration
    this.projectPrefix = config?.projectPrefix || '';
    this.tokenApiRoot = config?.tokenApiRoot;
    this.getFirebaseToken = config?.getFirebaseToken;
    this.tokenRefreshInterval = (config?.tokenRefreshInterval || 55) * 60 * 1000; // Convert to ms
    
    // Setup auth state listener
    this.setupAuthListener();
  }
  
  /**
   * Get Firebase token from external API
   */
  private async fetchFirebaseToken(): Promise<string> {
    // Prevent multiple simultaneous calls
    if (this.isFetchingToken) {
      console.log('Already fetching token, skipping duplicate call');
      // Wait for the current fetch to complete
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!this.isFetchingToken) {
            clearInterval(checkInterval);
            // Try to get cached token
            const cachedToken = localStorage.getItem('whiteboard_firebase_token');
            if (cachedToken) {
              resolve(cachedToken);
            } else {
              reject(new Error('Token fetch completed but no token found'));
            }
          }
        }, 100);
      });
    }
    
    this.isFetchingToken = true;
    
    try {
      if (this.getFirebaseToken) {
        const tokenData = await this.getFirebaseToken();
        this.isFetchingToken = false;
        
        // Handle both string token and object with firebaseToken property
        if (typeof tokenData === 'string') {
          return tokenData;
        } else if (tokenData && typeof tokenData === 'object' && 'firebaseToken' in tokenData) {
          // Store refresh token if available
          const tokenObj = tokenData as { firebaseToken: string; refreshToken?: string };
          if (tokenObj.refreshToken) {
            localStorage.setItem('whiteboard_refresh_token', tokenObj.refreshToken);
          }
          return tokenObj.firebaseToken;
        } else {
          throw new Error('Invalid token response from getFirebaseToken');
        }
      }
      
      if (!this.tokenApiRoot) {
        throw new Error('No token API root or getFirebaseToken function provided');
      }
      
      const tokenUrl = `${this.tokenApiRoot}/api/WhiteboardFirebase/GetToken`;
   
      
      // Generate device ID if not exists
      const deviceId = localStorage.getItem('whiteboard_device_id') || 
                      `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('whiteboard_device_id', deviceId);
    
    // Call token API endpoint
    const response = await fetch(tokenUrl, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        deviceId: deviceId,
        deviceName: 'Web Whiteboard',
        userId: null // Will be filled by the API from current user
      })
    });
    
  
    
    if (!response.ok) {
      const errorText = await response.text();
     
      throw new Error(`Failed to fetch Firebase token: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
   
    
      // Store refresh token for later use
      if (data.refreshToken) {
        localStorage.setItem('whiteboard_refresh_token', data.refreshToken);
      }
      
      // Cache the token
      localStorage.setItem('whiteboard_firebase_token', data.firebaseToken);
      
      this.isFetchingToken = false;
      return data.firebaseToken;
    } catch (error) {
      this.isFetchingToken = false;
      throw error;
    }
  }
  
  /**
   * Authenticate with Firebase using custom token
   */
  async authenticate(): Promise<User> {
    console.log('🔥 DynamicFirebaseService: Starting authentication...');
    try {
      const token = await this.fetchFirebaseToken();
      console.log('🔥 DynamicFirebaseService: Got Firebase token, signing in...');
      
      const userCredential = await signInWithCustomToken(this.auth, token);
      
      console.log('✅ DynamicFirebaseService: Authentication successful', userCredential.user.uid);
      
      // Setup token refresh
      this.setupTokenRefresh();
      
      return userCredential.user;
    } catch (error) {
      console.error('🔥 DynamicFirebaseService: Authentication failed:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
      }
      throw error;
    }
  }
  
  /**
   * Refresh Firebase token using refresh token
   */
  private async refreshFirebaseToken(): Promise<string> {
   
    
    if (!this.tokenApiRoot) {
      // Fallback to regular token fetch if no API root
      return this.fetchFirebaseToken();
    }
    
    const refreshToken = localStorage.getItem('whiteboard_refresh_token');
    const deviceId = localStorage.getItem('whiteboard_device_id');
    
    if (!refreshToken || !deviceId) {
      return this.fetchFirebaseToken();
    }
    
    const tokenUrl = `${this.tokenApiRoot}/api/WhiteboardFirebase/RefreshToken`;
    
    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: refreshToken,
          deviceId: deviceId
        })
      });
      
      if (!response.ok) {
        console.error('🔥 DynamicFirebaseService: Refresh failed, fetching new token');
        return this.fetchFirebaseToken();
      }
      
      const data = await response.json();
      
      // Store new refresh token
      if (data.refreshToken) {
        localStorage.setItem('whiteboard_refresh_token', data.refreshToken);
      }
      
     
      return data.firebaseToken;
    } catch (error) {
      console.error('🔥 DynamicFirebaseService: Refresh error, fetching new token:', error);
      return this.fetchFirebaseToken();
    }
  }
  
  /**
   * Setup automatic token refresh
   */
  private setupTokenRefresh() {
    // Clear existing timer
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }
    
    // Setup new timer (refresh 5 minutes before expiry)
    this.tokenRefreshTimer = setInterval(async () => {
      try {
        const token = await this.refreshFirebaseToken();
        await signInWithCustomToken(this.auth, token);
      } catch (error) {
        console.error('🔥 DynamicFirebaseService: Failed to auto-refresh token:', error);
      }
    }, this.tokenRefreshInterval);
  }
  
  /**
   * Setup auth state listener
   */
  private setupAuthListener() {
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
      } else {
      }
    });
  }
  
  /**
   * Get collection path with prefix
   */
  getCollectionPath(collectionName: string): string {
    return this.projectPrefix 
      ? `${this.projectPrefix}_${collectionName}` 
      : collectionName;
  }
  
  /**
   * Get storage path with prefix
   */
  getStoragePath(path: string): string {
    return this.projectPrefix 
      ? `${this.projectPrefix}/${path}` 
      : path;
  }
  
  /**
   * Get Firestore collection reference
   */
  getCollection(collectionName: string) {
    const path = this.getCollectionPath(collectionName);
    return collection(this.firestore, path);
  }
  
  /**
   * Get Firestore document reference
   */
  getDoc(collectionName: string, documentId: string) {
    const path = this.getCollectionPath(collectionName);
    return doc(this.firestore, path, documentId);
  }
  
  /**
   * Get Storage reference
   */
  getStorageRef(path: string) {
    const fullPath = this.getStoragePath(path);
    return ref(this.storage, fullPath);
  }
  
  /**
   * Cleanup resources
   */
  async cleanup() {
    // Clear token refresh timer
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }
    
    // Sign out
    await this.auth.signOut();
    
    // Delete app if it's not the default app
    if (this.appName !== 'default') {
      await deleteApp(this.app);
    }
  }
  
  // Getters for services
  get authService(): Auth {
    return this.auth;
  }
  
  get firestoreService(): Firestore {
    return this.firestore;
  }
  
  get storageService(): FirebaseStorage {
    return this.storage;
  }
  
  get currentUser(): User | null {
    return this.auth.currentUser;
  }
  
  get isAuthenticated(): boolean {
    return !!this.auth.currentUser;
  }
}

// Singleton instance for default Firebase
let defaultInstance: DynamicFirebaseService | null = null;

export function getDefaultFirebaseService(): DynamicFirebaseService {
  if (!defaultInstance) {
    defaultInstance = new DynamicFirebaseService();
  }
  return defaultInstance;
}

// Factory function for creating external Firebase instances
export function createExternalFirebaseService(config: FirebaseExternalConfig): DynamicFirebaseService {
  return new DynamicFirebaseService(config);
}